import React, { useState, useMemo } from 'react';
import { useAuthenticatedFetch } from '../../api/client';
import { CypherEditor } from './CypherEditor';
import { CypherTemplates } from './CypherTemplates';
import { deriveColumnHeader, formatCellValue, extractStepGraphData } from '../explorer/formatResults';
import { exportResultsToXlsx } from '../../utils/exportXlsx';
import type { GraphNode, GraphEdge } from '../../api/dastarkhan';
import {
  Button,
  Table,
  Card,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

interface CypherConsoleProps {
  projectId: string;
  onGraphData: (nodes: GraphNode[], edges: GraphEdge[]) => void;
  onClearGraphData: () => void;
  schema?: import('../../api/dastarkhan').GraphSchema | null;
  nodes?: GraphNode[];
}

export const CypherConsole: React.FC<CypherConsoleProps> = ({ projectId, onGraphData, onClearGraphData, schema, nodes: graphNodes }) => {
  const { theme } = useTheme();
  const { fetchData } = useAuthenticatedFetch();
  const [query, setQuery] = useState('');
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<{ data: Record<string, unknown>[]; columns: string[] } | null>(null);
  const [showTable, setShowTable] = useState(false);

  // AI suggest
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiSuggesting, setAiSuggesting] = useState(false);

  // AI fix suggestion
  const [fixSuggestion, setFixSuggestion] = useState<string | null>(null);
  const [fixLoading, setFixLoading] = useState(false);

  const handleExecute = async () => {
    if (!query.trim()) return;
    setExecuting(true);
    setError(null);
    try {
      const result = await fetchData(`/dastarkhan/projects/${projectId}/cypher`, {
        method: 'POST',
        body: JSON.stringify({ query: query.trim() }),
      }) as { data: Record<string, unknown>[]; columns: string[] } | undefined;
      if (result) {
        setResults(result);
        // Extract graph data and send to canvas
        try {
          const gd = extractStepGraphData(result.data);
          if (gd.nodes.length > 0) onGraphData(gd.nodes, gd.edges);
        } catch { /* non-graph results are fine */ }
      }
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || 'Query execution failed. Check syntax or try a different query.';
      setError(msg);
      setFixSuggestion(null);
    } finally {
      setExecuting(false);
    }
  };

  const handleAiSuggest = async () => {
    if (!aiPrompt.trim()) return;
    setAiSuggesting(true);
    try {
      const prompt = [
        'You are a Neo4j Cypher query expert. Generate a READ-ONLY Cypher query for a Neo4j graph database.',
        'Rules:',
        '- Return ONLY the Cypher query, no explanation or markdown.',
        '- The query MUST be read-only (no CREATE, MERGE, SET, DELETE).',
        '- MUST include a RETURN clause that returns full nodes and relationships (not just properties).',
        '- Use CONTAINS for name matching (names may be partial).',
        '- Use variable-length paths [*1..N] for depth queries.',
        '',
        'Examples:',
        '- "Find neighbors of Petrov at depth 3" →',
        '  MATCH (n)-[r*1..3]-(m) WHERE n.name CONTAINS "Petrov" RETURN DISTINCT n, r, m',
        '- "Show all entities connected to Shell Corp" →',
        '  MATCH (n {name: "Shell Corp"})-[r]-(m) RETURN n, r, m',
        '- "Find shortest path between Viktor and Cyprus Holdings" →',
        '  MATCH path = shortestPath((a)-[*..6]-(b)) WHERE a.name CONTAINS "Viktor" AND b.name CONTAINS "Cyprus Holdings" RETURN path',
        '',
        `User request: ${aiPrompt.trim()}`,
      ].join('\n');

      const response = await fetchData(`/dastarkhan/projects/${projectId}/chat`, {
        method: 'POST',
        body: JSON.stringify({
          message: prompt,
          session_id: crypto.randomUUID(),
          context_level: 'schema_data',
        }),
      }) as { answer: string; cypher_query?: string } | undefined;
      if (response) {
        const cypher = response.cypher_query || response.answer.match(/MATCH[\s\S]*?RETURN[\s\S]*?(?:\n|$)/)?.[0] || response.answer;
        setQuery(cypher.trim());
      }
    } catch {
      setError('AI suggestion failed. Try rephrasing your request.');
    } finally {
      setAiSuggesting(false);
    }
  };

  const handleSuggestFix = async () => {
    if (!error || !query.trim()) return;
    setFixLoading(true);
    try {
      const fixPrompt = [
        'You are a Neo4j Cypher query expert. A query failed and needs correction.',
        'Return ONLY the corrected Cypher query, no explanation or markdown.',
        'The query must be read-only and include a RETURN clause returning full nodes/relationships.',
        '',
        `Original user intent: "${aiPrompt.trim() || 'not specified'}"`,
        `Failed query: ${query.trim()}`,
        `Error message: ${error}`,
        '',
        'Provide a corrected version that fulfills the original intent.',
      ].join('\n');

      const response = await fetchData(`/dastarkhan/projects/${projectId}/chat`, {
        method: 'POST',
        body: JSON.stringify({
          message: fixPrompt,
          session_id: crypto.randomUUID(),
          context_level: 'schema_data',
        }),
      }) as { answer: string; cypher_query?: string } | undefined;
      if (response) {
        const cypher = response.cypher_query || response.answer.match(/MATCH[\s\S]*?RETURN[\s\S]*?(?:\n|$)/)?.[0] || response.answer;
        setFixSuggestion(cypher.trim());
      }
    } catch {
      // Silently fail — just keep showing the original error
    } finally {
      setFixLoading(false);
    }
  };

  // Formatted columns using AI Explorer's formatting utilities
  const resultColumns = useMemo(() => {
    if (!results) return [];
    return results.columns.map((col) => ({
      key: col,
      header: deriveColumnHeader(col, results.data),
      render: (value: unknown) => {
        const formatted = formatCellValue(value);
        if (formatted === '—') return <span style={{ color: theme.colors.text.secondary }}>—</span>;
        if (formatted.includes('\n')) {
          return (
            <pre style={{
              fontSize: '0.78rem',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
              whiteSpace: 'pre-wrap',
              margin: 0,
              color: theme.colors.text.primary,
            }}>{formatted}</pre>
          );
        }
        return <span style={{ fontSize: '0.8rem' }}>{formatted}</span>;
      },
    }));
  }, [results, theme]);

  const hasResults = results && results.data.length > 0 && !error;

  // Table export handlers
  const handleExportXlsx = async () => {
    if (!results) return;
    try {
      await exportResultsToXlsx(
        results.columns,
        results.data,
        (col, data) => deriveColumnHeader(col, data),
        (val) => formatCellValue(val),
        'cypher-results.xlsx',
      );
    } catch (err) {
      setError(err instanceof Error ? `XLSX export failed: ${err.message}` : 'XLSX export failed');
    }
  };

  const handleExportJson = () => {
    if (!results) return;
    try {
      const json = JSON.stringify(results, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cypher-results.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? `JSON export failed: ${err.message}` : 'JSON export failed');
    }
  };


  return (
    <Stack gap="sm">
      {/* Row 1: Query Templates (horizontal, at top) */}
      <CypherTemplates onApply={setQuery} schema={schema} nodes={graphNodes} />

      {/* Row 2: AI Cypher Suggest */}
      <Flex gap="xs" align="end">
        <div style={{ flex: 1 }}>
          <textarea
            value={aiPrompt}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAiPrompt(e.target.value)}
            placeholder="Describe what you want to find... (AI generates Cypher)"
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiSuggest(); } }}
            autoComplete="one-time-code"
            rows={2}
            style={{
              width: '100%',
              resize: 'vertical',
              fontSize: '0.85rem',
              fontFamily: "'Source Sans 3', sans-serif",
              padding: spacing.xs,
              border: `1px solid ${theme.colors.border.default}`,
              borderRadius: '4px',
              background: theme.colors.background.default,
              color: theme.colors.text.primary,
              lineHeight: 1.4,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={handleAiSuggest} disabled={aiSuggesting || !aiPrompt.trim()}>
          {aiSuggesting ? 'Thinking...' : 'AI Suggest'}
        </Button>
      </Flex>

      {/* Row 3: Cypher Editor */}
      <CypherEditor
        value={query}
        onChange={setQuery}
        onExecute={handleExecute}
        executing={executing}
        error={error}
      />

      {/* AI Fix Suggestion */}
      {error && (
        <Flex gap="xs" align="start" style={{ flexWrap: 'wrap' }}>
          <Button variant="outline" size="sm" onClick={handleSuggestFix} disabled={fixLoading}>
            {fixLoading ? 'Thinking...' : 'Suggest Fix'}
          </Button>
          {fixSuggestion && (
            <Card style={{ flex: 1, minWidth: '200px' }}>
              <Card.Body>
                <Stack gap="xs">
                  <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>AI Suggested Fix:</span>
                  <pre style={{
                    fontSize: '0.78rem',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
                    whiteSpace: 'pre-wrap',
                    margin: 0,
                    padding: spacing.xs,
                    background: theme.colors.background.paper,
                    borderRadius: '4px',
                    color: theme.colors.text.primary,
                  }}>{fixSuggestion}</pre>
                  <Button variant="solid" size="sm" onClick={() => { setQuery(fixSuggestion); setError(null); setFixSuggestion(null); }}>
                    Use Suggestion
                  </Button>
                </Stack>
              </Card.Body>
            </Card>
          )}
        </Flex>
      )}

      {/* Collapsible results table */}
      {results && (
        <div>
          <Flex align="center" gap="xs">
            <Button variant="ghost" size="sm" onClick={() => setShowTable(!showTable)}
              style={{ fontSize: '0.8rem', padding: '2px 6px' }}>
              {showTable ? '▾' : '▸'} {results.data.length} result{results.data.length !== 1 ? 's' : ''}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setResults(null); onClearGraphData(); }}
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}>
              Clear
            </Button>
          </Flex>
          {showTable && (
            <Card style={{ overflow: 'hidden', marginTop: spacing.xs }}>
              <Card.Body>
                <Stack gap="sm">
                  <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                    <Table data={results.data} columns={resultColumns} bordered emptyMessage="No results" />
                  </div>
                  {hasResults && (
                    <Flex gap="xs" align="center">
                      <Button variant="outline" size="sm" onClick={handleExportXlsx}>Export XLSX</Button>
                      <Button variant="outline" size="sm" onClick={handleExportJson}>Export JSON</Button>
                    </Flex>
                  )}
                </Stack>
              </Card.Body>
            </Card>
          )}
        </div>
      )}
    </Stack>
  );
};
